import { useCallback, useEffect, useRef, useState } from 'react';
import { ID } from '~/lib/model';
import { IPlacement } from '~/modules/Sources/models/Placement';
import { fetchPlacementsPage } from '~/modules/Sources/api';

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export function usePlacements(sourceId: ID | undefined) {
    const [items, setItems] = useState<IPlacement[]>([]);
    const [count, setCount] = useState(0);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [q, setQ] = useState('');
    const [bundle, setBundle] = useState('');
    const [platform, setPlatform] = useState('');
    const [adType, setAdType] = useState('');
    const [placementType, setPlacementType] = useState('');
    const [loading, setLoading] = useState(false);
    const reqId = useRef(0);

    const load = useCallback(() => {
        if (sourceId == null || sourceId === '') {
            setItems([]);
            setCount(0);
            return;
        }
        const id = ++reqId.current;
        setLoading(true);
        fetchPlacementsPage(sourceId, { pageNo, pageSize, q, bundle, platform, adType, placementType })
            .then(res => {
                if (id === reqId.current) {
                    setItems(Array.isArray(res?.items) ? res.items : []);
                    setCount(Number(res?.count) || 0);
                }
            })
            .finally(() => {
                if (id === reqId.current) setLoading(false);
            });
    }, [sourceId, pageNo, pageSize, q, bundle, platform, adType, placementType]);

    useEffect(() => {
        const timer = setTimeout(load, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [load]);

    const search = (next: { q?: string; bundle?: string; platform?: string; adType?: string; placementType?: string }) => {
        setPageNo(0);
        if (next.q !== undefined) setQ(next.q);
        if (next.bundle !== undefined) setBundle(next.bundle);
        if (next.platform !== undefined) setPlatform(next.platform);
        if (next.adType !== undefined) setAdType(next.adType);
        if (next.placementType !== undefined) setPlacementType(next.placementType);
    };

    const changePage = (page: number, perPage: number) => {
        setPageNo(page);
        setPageSize(perPage);
    };

    return { items, count, pageNo, pageSize, q, bundle, platform, adType, placementType, loading, search, changePage, reload: load };
}
